import {
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/16/solid';
import { format } from 'date-fns';

import { EncounterSchema } from '../../../services/types/treatment-types';
import { Badge } from '../../ui/catalyst/badge';
import { Button } from '../../ui/catalyst/button';
import { ProviderMentionsList } from './ProviderMentionsList';

interface EncounterDetailViewProps {
  encounter: EncounterSchema;
  onBack: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

export const EncounterDetailView = ({
  encounter,
  onBack,
  onEdit,
  onDelete,
}: EncounterDetailViewProps) => {
  const hasDate = Boolean(encounter.date);
  const formattedDate = hasDate
    ? format(new Date(encounter.date as string), 'MMMM d, yyyy')
    : '';

  const mentions = encounter.mentions || [];
  const hasMentions = mentions.length > 0;

  return (
    <div className="bg-white">
      <div className="border-b border-gray-100 sticky top-0 bg-white">
        <div className="px-4 py-3 flex items-center">
          <Button
            plain
            className="mr-3 text-gray-500 hover:text-gray-700 p-1 rounded hover:bg-gray-100 transition-colors"
            onClick={onBack}
            aria-label="Back to encounters list"
          >
            <ArrowLeftIcon className="h-4 w-4" />
          </Button>
          <div className="flex-grow">
            <div className="text-sm font-medium text-gray-900">
              {hasDate ? (
                formattedDate
              ) : (
                <span className="inline-flex items-center">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mr-1.5" />
                  Undated Encounter
                </span>
              )}
            </div>
          </div>
          <div className="flex space-x-2">
            <Button
              plain
              className="text-gray-500 hover:text-gray-700 p-1.5 rounded hover:bg-gray-100 transition-colors"
              onClick={onEdit}
              aria-label="Edit encounter"
            >
              <PencilIcon className="h-4 w-4" />
            </Button>
            <Button
              plain
              className="text-gray-500 hover:text-red-600 p-1.5 rounded hover:bg-gray-100 transition-colors"
              onClick={onDelete}
              aria-label="Delete encounter"
            >
              <TrashIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="px-6 py-4">
        <div className="mb-6">
          <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
            Description
          </h3>
          {encounter.description ? (
            <div
              className="text-sm leading-relaxed text-gray-800 whitespace-pre-line"
              style={{ lineHeight: '1.65' }}
            >
              {encounter.description}
            </div>
          ) : (
            <div className="text-sm text-gray-500 italic">
              No description provided
            </div>
          )}
        </div>

        {encounter.services && encounter.services.length > 0 && (
          <div className="mb-6">
            <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
              Services
            </h3>
            <div className="flex flex-wrap gap-1.5">
              {encounter.services.map((service, index) => (
                <Badge
                  key={index}
                  color="zinc"
                  className="text-xs px-2 py-0.5 font-normal bg-gray-100 text-gray-500"
                >
                  {service}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {hasMentions && (
          <div className="mb-6">
            <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
              Sources
            </h3>
            <ProviderMentionsList mentions={mentions} expandedByDefault />
          </div>
        )}
      </div>
    </div>
  );
};
